import NProgress from 'nprogress';
import { createRouter, createWebHistory } from 'vue-router';
import { addDays, subDays } from 'date-fns';
import store from '@/store';
import defineAbilities from '@/defineAbilities';
import { legacyRedirects } from './legacy-redirects';

import LoginView from '@/views/LoginView.vue';
/* Trackers */
import TrackersPage from '@/views/Pages/Trackers.vue';
import TrackerPage from '@/views/Pages/Tracker.vue';
import DevicesMapLayout from '@/layouts/DevicesMapLayout.vue';
import DeviceListView from '@/views/DeviceListView.vue';
import DeviceDetailsView from '@/views/DeviceDetailsView.vue';
import DeviceDetailsBar from '@/components/device/details/DeviceDetailsBar.vue';

/* Groups */
import GroupListView from '@/views/GroupListView.vue';
import GroupsPage from '@/views/Pages/Groups.vue';
import GroupPage from '@/views/Pages/Group.vue';

/* Geofences */
import GeofenceListView from '@/views/GeofenceListView.vue';
import GeofenceDetailsView from '@/views/GeofenceDetailsView.vue';
import GeofenceCreateView from '@/views/GeofenceCreateView.vue';
import GeofenceEditView from '@/views/GeofenceEditView.vue';
import GeofencesPage from '@/views/Pages/Geofences.vue';
import GeofencePage from '@/views/Pages/Geofence.vue';
import GeofenceEditPage from '@/views/Pages/GeofenceEdit.vue';
import GeofenceCreatePage from '@/views/Pages/GeofenceCreate.vue';

/* Overlays */
import OverlaysPage from '@/views/Pages/Overlays.vue';
import OverlayCreatePage from '@/views/Pages/OverlayCreate.vue';
import OverlayEditPage from '@/views/Pages/OverlayEdit.vue';

/* Anchors */
import AnchorsPage from '@/views/Pages/Anchors.vue';

/* Insights */
import InsightsPage from '@/views/Pages/Insights.vue';

/* Dashboards */
import DashboardsLayout from '@/layouts/DashboardsLayout.vue';
import DashboardsDwellView from '@/views/DashboardsDwellView.vue';
import DashboardsCustomReporstView from '@/views/DashboardsCustomReportsView.vue';
import DashboardsDwellDisclaimer from '@/components/dashboards/dwell/DashboardsDwellDisclaimer.vue';

/** Shipments */
import ShipmentsMapLayout from '@/layouts/ShipmentsMapLayout.vue';
import ShipmentDetailsView from '@/views/Shipments/ShipmentDetailsView.vue';
import ShipmentCreateView from '@/views/Shipments/ShipmentCreateView.vue';
import ShipmentTrackView from '@/views/Shipments/ShipmentTrackView.vue';

import ShipmentsPage from '@/views/Pages/Shipments.vue';
import ExceptionsPage from '@/views/Pages/Exceptions.vue';
import ShipmentPage from '@/views/Pages/Shipment.vue';
import ShipmentEditPage from '@/views/Pages/ShipmentEdit.vue';

/** Find Containers */
import FindContainersPage from '@/views/Pages/FindContainers.vue';

/** Alerts */
import AlertsView from '@/views/AlertsView.vue';

/** Reports */
import ReportListView from '@/views/Reports/ReportListView.vue';
import ReportDetailsView from '@/views/Reports/ReportDetailsView.vue';
import ReportViewView from '@/views/Reports/ReportViewView.vue';
import TrackerTrackView from '@/views/Trackers/TrackerTrackView.vue';

/** Rules */
import RulesEditView from '@/views/Rules/RulesEditView.vue';
import RulesCreateView from '@/views/Rules/RulesCreateView.vue';
import RulesManageView from '@/views/Rules/RulesManageView.vue';

/** Settings */
import SettingsLayout from '@/layouts/SettingsLayout.vue';
import SettingsNotificationsView from '@/views/Settings/SettingsNotificationsView.vue';
import SettingsTrackersView from '@/views/Settings/SettingsTrackersView.vue';
import SettingsShipmentsView from '@/views/Settings/SettingsShipmentsView.vue';
import SettingsGeofencesView from '@/views/Settings/SettingsGeofencesView.vue';
import SettingsGeofenceTypesView from '@/views/Settings/SettingsGeofenceTypesView.vue';
import SettingsContainerTypesView from '@/views/Settings/SettingsContainerTypesView.vue';
/** Keep the Manage* names: 20 call sites and legacy-redirects.js address these routes by name. */
import ManageUsersView from '@/views/Settings/ManageUsersView.vue';
import ManageAPIAccessView from '@/views/Settings/ManageAPIAccessView.vue';
import ManageSubscriptionsView from '@/views/Settings/ManageSubscriptionsView.vue';
import ManageSubscriptionView from '@/views/Settings/ManageSubscriptionView.vue';
import ManageOrdersView from '@/views/Settings/ManageOrdersView.vue';
import ManageOrderView from '@/views/Settings/ManageOrderView.vue';
import ManageDepositsView from '@/views/Settings/ManageDepositsView.vue';
import ManageAddressesView from '@/views/Settings/ManageAddressesView.vue';
import ManagePaymentView from '@/views/Settings/ManagePaymentView.vue';

/** Account */
import AccountSettingsView from '@/views/AccountSettingsView.vue';

/** Approve Order */
import ApproveOrderView from '@/views/ApproveOrderView.vue';
import ApproveOrderSuccessView from '@/views/ApproveOrderSuccessView.vue';

/** 40x */
import NotFoundView from '@/views/NotFoundView.vue';
import ForbiddenView from '@/views/ForbiddenView.vue';
import { useNextRoute } from '@/use/use-next-route';
import GroupDetailsView from '@/views/GroupDetailsView.vue';
import {
  fetchBleAnchors,
  fetchDevicesAndIccids,
  fetchGeofences,
  fetchGroup,
  fetchGroups,
  fetchPositionsByAccount,
  fetchDeviceById,
} from './fetchers';
import { useDevice } from '@/composables/useDevice';
import { useAbility } from '@casl/vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';

/**
 * The Personal group is everyone's, so /settings itself is ungated and only the account
 * panes send a non-admin back to Notifications rather than off the page.
 */
const accountSettingsGuard = () => {
  const { can } = useAbility();
  if (!can('manage', 'Account')) {
    return { name: 'SettingsNotificationsView' };
  }
};

const routes = [
  {
    path: '/',
    name: 'root',
    beforeEnter: (to) => {
      const { next } = to.query;
      if (next) {
        return {
          path: next,
        };
      } else {
        return {
          name: 'DeviceListView',
        };
      }
    },
  },
  {
    path: '/login',
    name: 'LoginView',
    component: LoginView,
  },
  {
    path: '/forgot',
    name: 'PasswordForgotView',
    component: () =>
      import(
        /* webpackChunkName: "forgot" */ '../views/PasswordForgotView.vue'
      ),
  },
  {
    path: '/reset-password',
    name: 'PasswordResetView',
    component: () =>
      import(
        /* webpackChunkName: "reset-password" */ '../views/PasswordResetView.vue'
      ),
  },
  {
    path: '/create-account',
    name: 'CreateAccountView',
    component: () =>
      import(
        /* webpackChunkName: "createAccount" */ '../views/CreateAccountView.vue'
      ),
  },
  {
    path: '/payment-success',
    name: 'PaymentSuccessView',
    component: () =>
      import(
        /* webpackChunkName: "createAccount" */ '../views/PaymentSuccessView.vue'
      ),
  },

  /** Trackers */
  {
    path: '/accounts/:accountId',
    component: DefaultLayout,
    children: [
      {
        path: 'trackers/:id',
        name: 'GpxAdminDeviceDetailsView',
        component: TrackerPage,
      },
    ],
  },
  {
    path: '/trackers',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'DeviceListView',
        component: TrackersPage,
      },
      {
        path: ':id',
        name: 'DeviceDetailsView',
        component: TrackerPage,
      },
    ],
  },

  /** Groups */
  {
    path: '/groups',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'GroupListView',
        component: GroupsPage,
      },
      {
        path: ':id',
        name: 'GroupDetailsView',
        component: GroupPage,
      },
    ],
  },

  /** Geofences */
  {
    path: '/geofences',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'GeofenceListView',
        component: GeofencesPage,
      },
      {
        path: ':id',
        name: 'GeofenceDetailsView',
        component: GeofencePage,
      },
      {
        path: 'create',
        name: 'GeofenceCreateView',
        component: GeofenceCreatePage,
      },
      {
        path: ':id/edit',
        name: 'GeofenceEditView',
        component: GeofenceEditPage,
      },
    ],
  },

  /** Overlays */
  {
    path: '/overlays',
    component: DefaultLayout,
    beforeEnter: () => {
      const { can } = useAbility();
      if (!can('manage', 'MapOverlay')) {
        return { name: 'DeviceListView' };
      }
    },
    children: [
      {
        path: '',
        name: 'OverlayListView',
        component: OverlaysPage,
      },
      {
        path: 'create',
        name: 'OverlayCreateView',
        component: OverlayCreatePage,
      },
      {
        path: ':id/edit',
        name: 'OverlayEditView',
        component: OverlayEditPage,
      },
    ],
  },

  /** Anchors */
  {
    path: '/anchors',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'AnchorsListView',
        component: AnchorsPage,
      },
    ],
  },

  /** Insights */
  {
    path: '/insights',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'InsightsView',
        component: InsightsPage,
      },
      {
        path: ':id',
        name: 'InsightDetailsView',
        component: InsightsPage,
      },
    ],
  },

  /** Dashboards */
  {
    path: '/dashboards',
    redirect: '/dashboards/dwell',
    component: DashboardsLayout,
    beforeEnter: () => {
      const { can } = useAbility();
      if (!can('view', 'Dashboard')) {
        return { name: 'DeviceListView' };
      }
    },
    children: [
      {
        path: 'dwell',
        name: 'DashboardsDwellView',
        components: {
          default: DashboardsDwellView,
          disclaimer: DashboardsDwellDisclaimer,
        },
      },
      {
        path: 'analytics',
        name: 'DashboardCustomReportView',
        components: {
          default: DashboardsCustomReporstView,
          disclaimer: DashboardsDwellDisclaimer,
        },
        beforeEnter: () => {
          if (!store.getters['auth/canViewCustomReport']) {
            return { name: 'DeviceListView' };
          }
        },
      },
    ],
  },

  /** Shipments */
  {
    path: '/archived-shipments',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'ShipmentArchivedListView',
        component: ShipmentsPage,
        meta: { requestAccountAccess: 'can_track_shipments' },
      },
    ],
  },

  {
    path: '/exceptions',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'ExceptionsListView',
        component: ExceptionsPage,
        meta: { requestAccountAccess: 'can_track_shipments' },
      },
    ],
  },

  {
    path: '/shipments',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'ShipmentListView',
        component: ShipmentsPage,
        meta: { requestAccountAccess: 'can_track_shipments' },
      },

      {
        path: ':id',
        name: 'ShipmentDetailsView',
        component: ShipmentPage,
        meta: { requestAccountAccess: 'can_track_shipments' },
      },
      {
        path: ':id/edit',
        name: 'ShipmentEditView',
        component: ShipmentEditPage,
        meta: {
          requestAccountAccess: 'can_track_shipments',
          requiresAbility: ['manage', 'Shipments'],
        },
      },
    ],
  },
  {
    path: '/shipments',
    component: ShipmentsMapLayout,
    children: [
      {
        path: 'track/:uuid',
        name: 'ShipmentTrackView',
        components: {
          default: ShipmentTrackView,
        },
      },
    ],
  },
  {
    path: '/shipments/create/:draftId(\\d+)',
    redirect: (to) => ({
      name: 'ShipmentEditView',
      params: { id: to.params.draftId },
    }),
  },
  {
    path: '/shipments/create',
    name: 'ShipmentCreateView',
    components: {
      default: ShipmentCreateView,
    },
    meta: {
      requestAccountAccess: 'can_track_shipments',
      requiresAbility: ['manage', 'Shipments'],
    },
  },

  /** Find Containers */
  {
    path: '/find-containers',
    component: DefaultLayout,
    beforeEnter: () => {
      const { can } = useAbility();
      if (!can('view', 'ContainerTypes')) {
        return { name: 'DeviceListView' };
      }
    },
    children: [
      {
        path: '',
        name: 'FindContainersView',
        component: FindContainersPage,
      },
    ],
  },

  /** Alerts */
  {
    path: '/alerts',
    name: 'AlertsView',
    component: AlertsView,
  },

  /** Reports */
  {
    path: '/reports',
    name: 'ReportListView',
    component: ReportListView,
  },
  {
    path: '/reports/:id',
    name: 'ReportDetailsView',
    component: ReportDetailsView,
  },
  {
    path: '/reports/view/:uuid',
    name: 'ReportViewView',
    component: ReportViewView,
  },

  /** Public tracker share link */
  {
    path: '/trackers/track/:uuid',
    name: 'TrackerTrackView',
    component: TrackerTrackView,
  },

  /** Rules */
  {
    path: '/rules',
    name: 'rules.index',
    components: {
      default: RulesManageView,
    },
    meta: { requestAccountAccess: 'has_rules_engine' },
  },
  {
    path: '/rules/create',
    name: 'rules.create',
    components: {
      default: RulesCreateView,
    },
    meta: { requestAccountAccess: 'has_rules_engine' },
    beforeEnter: () => {
      const { can } = useAbility();
      if (!can('manage', 'Rules')) {
        return { name: 'rules.index' };
      }
    },
  },
  {
    path: '/rules/:id',
    name: 'rules.edit',
    components: {
      default: RulesEditView,
    },
    meta: { requestAccountAccess: 'has_rules_engine' },
    beforeEnter: () => {
      const { can } = useAbility();
      if (!can('manage', 'Rules')) {
        return { name: 'rules.index' };
      }
    },
  },

  /** Manage — every tab now lives under /settings; the old paths redirect. */
  {
    path: '/manage',
    redirect: { name: 'ManageSubscriptionsView' },
    children: [
      { path: 'subscriptions', redirect: { name: 'ManageSubscriptionsView' } },
      {
        path: 'subscriptions/:id',
        redirect: (to) => ({
          name: 'ManageSubscriptionView',
          params: to.params,
        }),
      },
      { path: 'users', redirect: { name: 'ManageUsersView' } },
      { path: 'orders', redirect: { name: 'ManageOrdersView' } },
      {
        path: 'orders/:id',
        redirect: (to) => ({ name: 'ManageOrderView', params: to.params }),
      },
      { path: 'deposits', redirect: { name: 'ManageDepositsView' } },
      { path: 'addresses', redirect: { name: 'ManageAddressesView' } },
      { path: 'payment', redirect: { name: 'ManagePaymentView' } },
      { path: 'api', redirect: { name: 'ManageAPIAccessView' } },
      { path: 'tracker-sharing', redirect: { name: 'SettingsTrackersView' } },
    ],
  },

  /** Settings */
  {
    path: '/settings',
    redirect: { name: 'SettingsNotificationsView' },
    component: SettingsLayout,
    children: [
      {
        path: 'notifications',
        name: 'SettingsNotificationsView',
        component: SettingsNotificationsView,
      },
      {
        path: 'trackers',
        name: 'SettingsTrackersView',
        component: SettingsTrackersView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'container-types',
        name: 'SettingsContainerTypesView',
        component: SettingsContainerTypesView,
        beforeEnter: () => {
          const redirect = accountSettingsGuard();
          if (redirect) return redirect;

          const { can } = useAbility();
          if (!can('manage', 'ContainerTypes')) {
            return { name: 'SettingsNotificationsView' };
          }
        },
      },
      {
        path: 'shipments',
        name: 'SettingsShipmentsView',
        component: SettingsShipmentsView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'geofences',
        name: 'SettingsGeofencesView',
        component: SettingsGeofencesView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'geofence-types',
        name: 'SettingsGeofenceTypesView',
        component: SettingsGeofenceTypesView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'users',
        name: 'ManageUsersView',
        component: ManageUsersView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'subscriptions',
        name: 'ManageSubscriptionsView',
        component: ManageSubscriptionsView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'subscriptions/:id',
        name: 'ManageSubscriptionView',
        component: ManageSubscriptionView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'orders',
        name: 'ManageOrdersView',
        component: ManageOrdersView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'orders/:id',
        name: 'ManageOrderView',
        component: ManageOrderView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'deposits',
        name: 'ManageDepositsView',
        component: ManageDepositsView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'addresses',
        name: 'ManageAddressesView',
        component: ManageAddressesView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'payment',
        name: 'ManagePaymentView',
        component: ManagePaymentView,
        beforeEnter: accountSettingsGuard,
      },
      {
        path: 'api',
        name: 'ManageAPIAccessView',
        component: ManageAPIAccessView,
        beforeEnter: () => {
          const redirect = accountSettingsGuard();
          if (redirect) return redirect;

          const { can } = useAbility();
          if (!can('manage', 'ApiTokens') && !can('view', 'LegacyApiKey')) {
            return { name: 'SettingsNotificationsView' };
          }
        },
      },
    ],
  },

  /** Account */
  {
    path: '/account',
    name: 'AccountSettingsView',
    component: AccountSettingsView,
  },

  /** Approve order */
  {
    path: '/approve-order-success',
    name: 'ApproveOrderSuccessView',
    component: ApproveOrderSuccessView,
  },
  {
    path: '/approve-order/:uuid',
    name: 'ApproveOrderView',
    component: ApproveOrderView,
  },

  /** Legacy app redirects */
  ...legacyRedirects,

  /** 40x */
  {
    path: '/403',
    name: 'ForbiddenView',
    component: ForbiddenView,
  },
  {
    path: '/404',
    name: 'NotFoundView',
    component: NotFoundView,
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/404',
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(f, t, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return {
      top: 0,
    };
  },
});

router.beforeEach(async (to, from) => {
  NProgress.start();
  const accountId = store.getters['auth/accountId'];
  const account = store.getters['auth/account'];
  const isB2bAccount = store.getters['auth/isB2B'];

  const deviceStore = useDevice();
  const { fetchByAccountId: fetchDevicesByAccountId } = deviceStore;
  const { devices } = deviceStore;

  const defaultStatuses = 'active,pending,pending-renewal,pending-cancellation';
  const subscriptionStatuses = isB2bAccount
    ? defaultStatuses.concat(',', 'cancelled')
    : defaultStatuses;

  const publicPages = [
    'PasswordForgotView',
    'PasswordResetView',
    'ApproveOrderView',
    'ApproveOrderSuccessView',
    'ShipmentTrackView',
    'ReportViewView',
    'CreateAccountView',
    'TrackerTrackView',
  ];

  if (publicPages.includes(to.name)) {
    return;
  }

  if (!store.getters['auth/check'] && to.name !== 'LoginView') {
    const { next } = useNextRoute(to);
    const query = next ? { next } : {};
    return {
      name: 'LoginView',
      query,
    };
  }

  if (
    to.meta?.requestAccountAccess &&
    !account[to.meta?.requestAccountAccess]
  ) {
    return {
      name: 'DeviceListView',
    };
  }

  if (to.meta?.requiresAbility) {
    const [action, subject] = to.meta.requiresAbility;
    if (!defineAbilities().can(action, subject)) {
      return {
        name: 'DeviceListView',
      };
    }
  }

  /** Search history push handler */
  if (to.name === from.name) {
    return;
  }

  /** Special campaigns */
  fetchDevicesAndIccids({ accountId });

  if (to.name === 'DeviceListView') {
    // Devices are now fetched via vue-query in Trackers.vue
    // Only fetch positions, groups, and geofences
    if (from.name === 'GroupListView') {
      return;
    }

    if (devices.size > 1) {
      return;
    }

    if (accountId) {
      await Promise.all([
        fetchPositionsByAccount(accountId),
        fetchGroups({ accountId }),
        fetchGeofences({ accountId }),
      ]);
    }
  }

  if (to.name === 'GroupListView') {
    if (!isB2bAccount) {
      return {
        name: 'DeviceListView',
      };
    }
    if (['DeviceListView', 'DeviceDetailsView'].includes(from.name)) {
      return;
    }
    // Devices are now fetched via vue-query in Trackers.vue (when coming from DeviceListView)
    // But for GroupListView we may still need devices, so keep the fetch for now
    // TODO: Consider refactoring GroupListView to also use vue-query
    const canSeeCancelledDevices = store.getters['auth/canSeeCancelledDevices'];
    const defaultStatuses =
      'active,pending,pending-renewal,pending-cancellation';
    const subscriptionStatuses = canSeeCancelledDevices
      ? defaultStatuses.concat(',', 'cancelled,blocked')
      : defaultStatuses;
    await Promise.all([
      fetchPositionsByAccount(accountId),
      // All three customer-facing statuses (ENG-2404) so the Group editing
      // modal opened from this list can resolve provisioned and archived
      // members of any group via deviceStore.getById.
      fetchDevicesByAccountId(accountId, {
        subscription_status: subscriptionStatuses,
        device_status: 'active,provisioned,hidden',
      }),
      fetchGroups({ accountId }),
      fetchGeofences({ accountId }),
    ]);
  }

  if (to.name === 'DeviceDetailsView') {
    // Device is now fetched via vue-query in Tracker.vue
    // Only fetch geofences if needed
    if (
      [
        'DeviceListView',
        'GeofenceCreateView',
        'GroupListView',
        'GroupDetailsView',
      ].includes(from.name)
    ) {
      return;
    }
    await Promise.all([fetchGeofences({ accountId })]);
  }
  if (to.name === 'GroupDetailsView') {
    if (!isB2bAccount) {
      return {
        name: 'DeviceListView',
      };
    }
    await fetchGroup({ accountId, id: to.params.id });
    if (from.name === 'GroupListView') {
      return;
    }
    // if (from.name === 'DeviceDetailsView') {
    //   return;
    // }
    await Promise.all([
      fetchPositionsByAccount(accountId),
      fetchDevicesByAccountId(accountId, {
        subscription_status: subscriptionStatuses,
      }),
      fetchGeofences({ accountId }),
    ]);
  }

  if (to.name === 'GpxAdminDeviceDetailsView') {
    if (!store.getters['auth/isGpxAdmin']) {
      return {
        name: from.name,
      };
    }
    const { accountId, id } = to.params;
    await Promise.all([
      fetchGeofences({ accountId }),
      fetchDeviceById(+accountId, +id, {
        with_current_position: true,
        include: 'subscription',
      }),
    ]);
  }

  if (to.name === 'GeofenceListView') {
    if (
      [
        'GeofenceDetailsView',
        'GeofenceCreateView',
        'GeofenceEditView',
      ].includes(from.name)
    ) {
      return;
    }
    await Promise.all([
      fetchPositionsByAccount(accountId),
      fetchDevicesByAccountId(accountId, {
        subscription_status: subscriptionStatuses,
      }),
      fetchGeofences({ accountId }),
      fetchBleAnchors({ accountId }),
    ]);
  }

  if (to.name === 'GeofenceDetailsView') {
    // fetchGeofence removed - data is now fetched via vue-query on the page
    if (
      [
        'GeofenceListView',
        'GeofenceEditView',
        'DeviceListView',
        'DeviceDetailsView',
        'GroupListView',
        'GroupDetailsView',
      ].includes(from.name)
    ) {
      return;
    }
    fetchPositionsByAccount(accountId);
    await fetchDevicesByAccountId(accountId, {
      subscription_status: subscriptionStatuses,
    });
    fetchGeofences({ accountId });
    fetchBleAnchors({ accountId });
  }

  if (to.name === 'GeofenceCreateView') {
    if (['DeviceDetailsView', 'GeofenceListView'].includes(from.name)) {
      return;
    }
    fetchBleAnchors({ accountId });
    await Promise.all([
      fetchPositionsByAccount(accountId),
      fetchDevicesByAccountId(accountId, {
        subscription_status: subscriptionStatuses,
      }),
      fetchGeofences({ accountId }),
    ]);
  }
  if (to.name === 'GeofenceEditView') {
    if (!['GeofenceDetailsView', 'GeofenceListView'].includes(from.name)) {
      return {
        name: 'GeofenceDetailsView',
        params: {
          id: to.params.id,
        },
      };
    }
  }

  if (to.name === 'SettingsNotificationsView') {
    await store.dispatch('settings/fetchByAccountId', { accountId });
  }

  if (to.name === 'ReportListView') {
    const accountId = store.getters['auth/accountId'];
    if (from.name === 'DeviceListView') {
      await store.dispatch('report/fetchByAccountId', {
        accountId,
        params: {
          include: 'device',
        },
      });
      return;
    }
    if (from.name === 'DeviceDetailsView') {
      await store.dispatch('report/fetchByAccountId', {
        accountId,
        params: {
          include: 'device',
        },
      });
      return;
    }
    if (from.name === 'ReportDetailsView') {
      return;
    }
    await store.dispatch('report/fetchByAccountId', {
      accountId,
      params: {
        include: 'device',
      },
    });
  }

  if (to.name === 'ReportDetailsView') {
    const accountId = store.getters['auth/accountId'];
    if (!from.name) {
      await fetchDevicesByAccountId(accountId, {
        subscription_status: 'active,pending-renewal',
      });
      await Promise.all([
        store.dispatch('report/fetchByAccountIdAndId', {
          accountId,
          id: +to.params.id,
          params: {
            include: 'device',
          },
        }),
        store.dispatch('report/fetchByAccountId', {
          accountId,
          params: {
            include: 'device',
          },
        }),
      ]);
    }
  }

  if (to.name === 'ManageAddressesView') {
    await store.dispatch('address/fetchByAccountId', { accountId });
  }
});

router.afterEach(() => {
  NProgress.done();
  store.dispatch('settings/closeMobileNav');
});

export default router;
